<template  class="sticky top-0 w-full bg-white/80 backdrop-blur-sm z-10" >
  <header class="z-10">

    <div class="max-w-full pb-1 px-2">
      <div
        :class="{
          'pt-4': !store.isAuthenticated,
          'pt-3': store.isAuthenticated,
          'flex items-center justify-between w-full': true
        }">

        <!-- Left: Ellipsis/Menu Button -->
        <div class="flex-none">
          <MobileTableEllipsisMenu 
            v-if="store.currentWatchListId" 
            @delete-watchlist="deleteWatchListButton"
          />
          <!-- <button v-else class="p-2 text-gray-600 hover:text-gray-800" aria-label="Menu">
            <i class="fas fa-ellipsis-h fa-fw text-xl"></i>
          </button> -->
        </div>

        <!-- Middle: Watchlist Name (Non-Editable) -->
        <div v-if="store.currentWatchListId && store.currentWatchList" class="flex-grow text-center min-w-0 mx-2 max-w-[60%] relative">
          <div
            class="text-lg font-light text-gray-700 truncate w-full"
            v-tippy="{
              content: store.currentWatchList.name,
              trigger: 'click',
            }"
          >
            {{ store.currentWatchList.name }}
          </div>
        </div>

        <!-- Right: Create Watchlist Alert Button -->
        <div class="flex-none">
          <button
            v-if="store.currentWatchListId"
            @click="handleCreateWatchlistAlert(SubscriptionScope.WATCHLIST)"
            class="flex items-center justify-center p-2 text-gray-600 hover:text-gray-800 rounded-md border border-transparent hover:border-gray-200"
            type="button"
            title="Create alert for this watchlist"
          >
            <img src="/assets/bell-plus-thick.svg" class="w-6 h-6" />
          </button>
          <div v-else class="w-10 h-10"></div>
        </div>
      </div>
    </div>
  </header>

  <!-- Watchlist Delete Confirmation Modal -->
  <TransitionRoot appear :show="isWatchlistDeleteModalOpen" as="template">
    <Dialog as="div" @close="closeWatchlistDeleteModal" class="relative z-50">
      <div class="fixed inset-0 bg-black/30" />
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6">
            <DialogTitle class="text-lg font-medium mb-4">Confirm Deletion</DialogTitle>
            <p class="mb-6">Are you sure you want to delete this watchlist and all associated alerts? This action cannot
              be undone.</p>
            <div class="flex justify-end space-x-3">
              <button @click="closeWatchlistDeleteModal" class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200">
                Cancel
              </button>
              <button 
                @click="confirmWatchlistDelete"
                :disabled="isDeletingWatchlist"
                class="flex items-center px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i v-if="isDeletingWatchlist" class="fas fa-spinner fa-spin mr-2"></i>
                Delete
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { useRoute } from 'vue-router';
import LoginMenu from './menus/LoginMenu.vue';
import { isMobile } from '../store';
import * as Sentry from '@sentry/vue';
import {
  toggleNotifications,
  fetchNotifications,
  store,
  unreadNotificationsCount,
  updateWatchList,
  deleteWatchList,
  showDialog,
  handleCreateWatchlistAlert,
} from '../store';

import EditableInput from './EditableInput.vue';
import { SubscriptionScope } from '../types/Subscriptions';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from '@headlessui/vue';

import { size } from 'lodash';
import MobileTableEllipsisMenu from './menus/MobileTableEllipsisMenu.vue';

export default {
  name: 'TopBar',
  components: {
    LoginMenu,
    EditableInput,
    Dialog,
    DialogTitle,
    DialogPanel,
    TransitionRoot,
    MobileTableEllipsisMenu,
  },
  data() {
    return {
      store,
      SubscriptionScope,
      ws: null,
      isEditing: false,
      previousWatchListName: '',
      disableBlur: false,
      isSaving: false,
      isWatchlistDeleteModalOpen: false,
      isDeletingWatchlist: false,
    }
  },
  setup() {
    const route = useRoute();

    return {
      route,
    }
  },
  computed: {
    breadcrumbPath() {
      const path = this.route.path

      // For lists page
      if (path.startsWith('/l/')) {
        return [{
          text: 'My Lists',
          icon: 'fas fa-list',
          link: '#'
        }, {
          text: store.watchLists.find(item => item.id === this.route.params.listId)?.name || 'List'
        }]
      }

      // For market page
      if (path.startsWith('/polymarket/')) {
        return [{
          text: this.route.params.marketName || 'Market',

        }, {
          text: this.route.params.marketSlug || ''
        }]
      }

      return []
    }
  },
  methods: {
    isMobile,
    shouldShowLoginMenu() {
      return !this.store.isAuthenticated && !this.isMobile();
    },
    handleCreateWatchlistAlert,
    toggleNotifications,
    size,
    unreadNotificationsCount,
    toggleSidebar() {
      store.isSidebarOpen = !store.isSidebarOpen;
    },
    handleAddNew() {
      this.$router.push('/');
    },

    /*
      Watchlist Name Edit
      ------------------------------------------------------------
    */
    async startEditing() {
      this.previousWatchListName = store.currentWatchList.name;
      this.isEditing = true;
      this.$nextTick(() => {
        if (this.$refs.editableInput) {
          const el = this.$refs.editableInput.$el || this.$refs.editableInput;
          const input = el.querySelector('input') || el;
          if (input && typeof input.focus === 'function') {
            input.focus();
            if (typeof input.select === 'function') {
              input.select();
            }
          }
        }
      });
    },

    async saveKeydown(event) {
      this.disableBlur = true;
      await this.saveEdit(event);
      this.$nextTick(() => {
        this.disableBlur = false;
      });
    },

    async saveBlur(event) {
      if (this.disableBlur) {
        this.disableBlur = false;
        return;
      }
      await this.saveEdit(event);
    },

    async saveEdit(event) {
      const newName = event.target.value.trim();

      if (newName !== '' && newName !== this.previousWatchListName && store.currentWatchListId) {
        store.currentWatchList.name = newName;
        this.isSaving = true;
        try {
          let success = await updateWatchList(store.currentWatchListId, {
            name: newName
          });
          if (success) {
            store.tableState.name = newName;
          } else {
            throw new Error('Failed to update watchlist name');
          }
        } catch (error) {
          Sentry.setExtra('method', 'saveEdit');
          Sentry.captureException(error);
          console.error("Error saving watchlist name:", error);
          showDialog({
            message: `Failed to update watchlist name`,
            type: 'error',
            timeout: 3000
          });
          store.currentWatchList.name = this.previousWatchListName;
        } finally {
          this.isSaving = false;
          this.previousWatchListName = '';
        }
      }
      this.isEditing = false;
    },

    discardEdit() {
      this.isEditing = false;
    },

    deleteWatchListButton() {
      this.isWatchlistDeleteModalOpen = true;
    },

    /*
      Subscription Delete
    */
    closeWatchlistDeleteModal() {
      this.isWatchlistDeleteModalOpen = false;
    },

    async confirmWatchlistDelete() {
      if (size(store.currentWatchListId) > 0) {
        this.isDeletingWatchlist = true;
        try {
          let success = await deleteWatchList(store.currentWatchListId);
          if (success) {
            showDialog({
              message: `Watchlist deleted successfully`,
              type: 'success',
              timeout: 3000
            });
            if (store.watchLists) {
              store.watchLists = store.watchLists.filter(item => item.id !== store.currentWatchListId)
            }
            if (store.notifications) {
              store.notifications = store.notifications.filter(item => item.watchlistId !== store.currentWatchListId)
            }

            this.$router.push('/');
          } else {
            throw new Error('Failed to delete watchlist');
          }
        } catch (error) {
          Sentry.setExtra('method', 'confirmWatchlistDelete');
          Sentry.captureException(error);
          console.error("Error deleting watchlist:", error);
           showDialog({
            message: `An unexpected error occurred while deleting the watchlist.`,
            type: 'error',
            timeout: 3000
          });
        } finally {
          this.isDeletingWatchlist = false;
        }
      }
      this.closeWatchlistDeleteModal();
    },
  },
  watch: {
    'store.notifications': {
      handler() {
        const count = this.unreadNotificationsCount();
        document.title = count > 0 ? `(${count}) Nevua Markets` : 'Nevua Markets';
      },
      deep: true
    },
    'store.isAuthenticated': {
      async handler(newValue, oldValue) {
        if (newValue == true && oldValue == false && store.notifications.length === 0) {
          await fetchNotifications();
        }
      }
    }
  },
  async mounted() {
    await fetchNotifications();
    
    
  }
}
</script>